import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, ChevronLeft, ExternalLink, Eye, Pencil } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { TiptapEditor } from "../../components/TiptapEditor";
import { listCmsPages, saveCmsPage } from "../../lib/adminApi";

const TABS = [
  { slug: "privacy", label: "Privacy Policy" },
  { slug: "refund", label: "Refund Policy" },
  { slug: "terms", label: "Terms & Conditions" },
  { slug: "about", label: "About Us" },
  { slug: "contact", label: "Contact Us" },
];

/** Admin → Pages. Simple side-by-side HTML editor + live preview for the 5 footer pages.
 *  Body is stored as raw HTML — no sanitisation; admins are trusted. */
export function AdminPagesPage() {
  const [pages, setPages] = useState({});    // { slug: {title, body_html, updated_at} }
  const [active, setActive] = useState("privacy");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  // Track dirty fields locally so users can edit multiple tabs before saving.
  const [drafts, setDrafts] = useState({});  // { slug: {title, body_html} } — only when edited

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await listCmsPages();
      const map = {};
      for (const p of rows) map[p.slug] = p;
      setPages(map);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't load pages.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const current = drafts[active] || pages[active] || { title: "", body_html: "" };
  const dirty = !!drafts[active];

  function setField(key, value) {
    setDrafts((prev) => ({
      ...prev,
      [active]: {
        title: prev[active]?.title ?? pages[active]?.title ?? "",
        body_html: prev[active]?.body_html ?? pages[active]?.body_html ?? "",
        [key]: value,
      },
    }));
  }

  async function handleSave() {
    if (!dirty) return;
    setSaving(true);
    try {
      const updated = await saveCmsPage(active, {
        title: current.title,
        body_html: current.body_html,
      });
      setPages((prev) => ({ ...prev, [active]: updated }));
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[active];
        return next;
      });
      toast.success(`${TABS.find((t) => t.slug === active).label} saved.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-6" data-testid="admin-pages-page">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <Link to="/admin" className="inline-flex items-center gap-1 text-xs text-muted hover:text-gold">
            <ChevronLeft className="h-3.5 w-3.5" /> Back to Admin
          </Link>
        </div>
        <p className="text-xs uppercase tracking-widest text-gold">Content</p>
        <h1 className="mt-1 text-3xl font-semibold text-text">Pages</h1>
        <p className="max-w-2xl text-sm text-muted">
          Edit the five footer pages — Privacy Policy, Refund Policy, Terms & Conditions,
          About Us, Contact Us. Body accepts raw HTML; a simple set of paragraph, heading and
          link tags is enough. Save each tab separately.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-border">
        {TABS.map((t) => {
          const isDirty = !!drafts[t.slug];
          return (
            <button
              key={t.slug}
              type="button"
              onClick={() => setActive(t.slug)}
              data-testid={`pages-tab-${t.slug}`}
              className={`inline-flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                active === t.slug
                  ? "border-gold text-gold"
                  : "border-transparent text-muted hover:text-text"
              }`}
            >
              {t.label}
              {isDirty && <span className="h-1.5 w-1.5 rounded-full bg-amber-400" title="Unsaved changes" />}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="flex flex-col gap-5 rounded-lg border border-border bg-card p-5">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="page-title">Page title</Label>
            <Input
              id="page-title"
              value={current.title}
              onChange={(e) => setField("title", e.target.value)}
              data-testid="pages-title-input"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <Label>Body</Label>
              <button
                type="button"
                onClick={() => setPreviewMode((v) => !v)}
                data-testid="pages-toggle-preview"
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-elevated px-2.5 py-1 text-xs text-muted transition-colors hover:border-gold/40 hover:text-gold"
              >
                {previewMode ? (
                  <><Pencil className="h-3 w-3" /> Edit</>
                ) : (
                  <><Eye className="h-3 w-3" /> Preview</>
                )}
              </button>
            </div>
            {previewMode ? (
              <div
                data-testid="pages-preview"
                className="prose prose-invert max-w-none min-h-[24rem] rounded-md border border-border bg-elevated/40 p-4 text-sm text-text [&_a]:text-gold [&_h2]:text-text [&_h3]:text-text"
                // Body copy is admin-owned; renders exactly as saved on /page/:slug.
                dangerouslySetInnerHTML={{ __html: current.body_html || "<p class='text-muted'>Nothing to preview yet.</p>" }}
              />
            ) : (
              <TiptapEditor
                value={current.body_html}
                onChange={(html) => setField("body_html", html)}
                testId="pages-editor"
              />
            )}
            <p className="text-[11px] text-muted">
              Rich text editor — use the toolbar for headings, links, lists and images. Content
              is stored as HTML and rendered verbatim on the public page.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              onClick={handleSave}
              disabled={!dirty || saving}
              data-testid="pages-save"
            >
              {saving ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…</>
              ) : (
                <><Save className="mr-2 h-4 w-4" /> Save {TABS.find((t) => t.slug === active).label}</>
              )}
            </Button>
            <a
              href={`/page/${active}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-muted hover:text-gold"
              data-testid="pages-view-live"
            >
              View live page <ExternalLink className="h-3 w-3" />
            </a>
            {pages[active]?.updated_at && (
              <span className="text-[11px] text-muted">
                Last saved: {new Date(pages[active].updated_at).toLocaleString()}
                {pages[active].updated_by ? ` by ${pages[active].updated_by}` : ""}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPagesPage;
